/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckCircleFilled,
  FilterOutlined,
  HourglassFilled,
  InfoCircleFilled,
  LikeFilled,
  RocketOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  message,
  Tooltip,
  TreeSelect,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { listOpportunities, reveal } from 'services/api/domains';
import { createCheckoutSession } from 'services/api/users';
import styles from 'styles/components/browseBacklinksOpportunities.module.scss';
import Loader from './loader';
import RequestBacklinkModal from './requestBacklinkModal';
import {
  MAX_CONCURRENT_REQUESTS,
  PLAN,
  REQUEST_TYPE,
  SESSION_PENDING_PRO_CHECKOUT,
} from 'utils/constants';
import {
  handleDomainAccessError,
  parseOpportunitiesListResponse,
} from 'utils/domainAccessErrors';
import { hasActiveBacklinkRequest } from 'utils/utils';
import { useSearchParams } from 'react-router-dom';
import DomainMetrics from './domainMetrics';
import KarmaIcon from './karmaIcon';
import pluralize from 'pluralize';
import CategoryContext from 'services/categoryContext';
import DomainContext from 'services/domainContext';
import ProfileContext from 'services/profileContext';
import { usePostHogFeatureFlag } from 'hooks/usePostHogFeatureFlag';

const BrowseBacklinksOpportunities = ({ domain, refreshDomainDetails }) => {
  const proPlansEnabled = usePostHogFeatureFlag('pro-plan');
  const { profile } = useContext(ProfileContext);
  const isPro = profile?.plan === PLAN.PRO;
  const [onlyGoodMatchFilter, setOnlyGoodMatchFilter] = useState(false);
  // Default off: pending preview rows have canGive/canReceive false, so the old
  // default (true) hid every opportunity for free TO_REVIEW users.
  const [onlyAvailableFilter, setOnlyAvailableFilter] = useState(false);
  const [onlyYCFilter, setOnlyYCFilter] = useState(false);
  const [hideSlowResponders, setHideSlowResponders] = useState(false);

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const { categories, categoriesFlat } = useContext(CategoryContext);
  const [opportunities, setOpportunities] = useState(null);
  const [upgradeCta, setUpgradeCta] = useState(false);
  const [metricsPending, setMetricsPending] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [requestType, setRequestType] = useState(REQUEST_TYPE.GIVE);
  const [searchInputValue, setSearchInputValue] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredOpportunities, setFilteredOpportunities] = useState(null);
  const { updateDomains } = useContext(DomainContext);

  const listOpportunitiesCall = () => {
    setLoadingOpportunities(true);
    listOpportunities({
      domain: domain.name,
      categories: selectedCategories,
      ...(hideSlowResponders ? { hideSlowResponders: true } : {}),
    })
      .then(({ data }) => {
        const parsed = parseOpportunitiesListResponse(data);
        setOpportunities(parsed.opportunities);
        setUpgradeCta(parsed.upgradeCta);
        setMetricsPending(parsed.metricsPending);
        filterOpportunities(parsed.opportunities);
      })
      .catch((e) => {
        console.error(e);
        setOpportunities([]);
        setFilteredOpportunities([]);
        if (!handleDomainAccessError(e, { proPlansEnabled })) {
          message.error('Could not load opportunities. Please try again.');
        }
      })
      .finally(() => {
        setLoadingOpportunities(false);
      });
  };

  const handleUpgradeToPro = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await createCheckoutSession();
      sessionStorage.setItem(SESSION_PENDING_PRO_CHECKOUT, '1');
      window.location.href = data.url;
    } catch (err) {
      message.error('Could not start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const init = () => {
    if (!opportunities && !loadingOpportunities) {
      listOpportunitiesCall();
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    listOpportunitiesCall();
  }, [domain]);

  useEffect(() => {
    const categParams = searchParams.get('categories');
    if (categParams?.length > 0) {
      setSelectedCategories(categParams.split(','));
    } else {
      setSelectedCategories((prev) => (prev.length === 0 ? prev : []));
    }

    const isGoodMatchParams = searchParams.get('onlyGoodMatch');
    if (isGoodMatchParams != null && isGoodMatchParams.length > 0) {
      setOnlyGoodMatchFilter(isGoodMatchParams !== 'false');
    }

    // Only sync from URL when present — do not force true (wipes pending preview).
    const isAvailableParams = searchParams.get('onlyAvailable');
    if (isAvailableParams != null && isAvailableParams.length > 0) {
      setOnlyAvailableFilter(isAvailableParams !== 'false');
    }

    const onlyYCParams = searchParams.get('onlyYC');
    if (onlyYCParams != null && onlyYCParams.length > 0) {
      setOnlyYCFilter(onlyYCParams !== 'false');
    }

    const hideSlowParams = searchParams.get('hideSlowResponders');
    if (hideSlowParams != null && hideSlowParams.length > 0) {
      setHideSlowResponders(hideSlowParams !== 'false');
    }
  }, [searchParams]);

  const onChange = (value) => {
    setSearchParams((prev) => {
      prev.set('categories', value);
      return prev;
    });
  };

  useEffect(() => {
    listOpportunitiesCall();
  }, [selectedCategories, hideSlowResponders]);

  useEffect(() => {
    filterOpportunities();
  }, [
    searchInputValue,
    onlyGoodMatchFilter,
    onlyAvailableFilter,
    onlyYCFilter,
  ]);

  const filterOpportunities = (opps = null) => {
    let filtered = opps || opportunities;

    if (searchInputValue && searchInputValue.length > 0) {
      filtered = filtered?.filter(
        (e) => `${e.description?.toLowerCase()}`.search(searchInputValue) !== -1
      );
    }
    if (onlyGoodMatchFilter) {
      filtered = filtered?.filter((e) => getIsGoodMatch(e));
    }
    if (onlyAvailableFilter && filtered?.length) {
      const availableOnly = filtered.filter((e) => getIsAvailable(e));
      // Pending preview: every row can have canGive/canReceive false. Never
      // collapse a non-empty API result to zero via Only Available alone.
      if (availableOnly.length > 0) {
        filtered = availableOnly;
      }
    }
    if (onlyYCFilter && domain.isYC) {
      filtered = filtered?.filter((e) => e.isYC);
    }
    setFilteredOpportunities(filtered);
  };

  const getIsAvailable = (opportunity) =>
    (opportunity.canGive || opportunity.canReceive) &&
    !hasActiveBacklinkRequest(opportunity);

  // Pending soft-entry only — never infer from canGive/canReceive (approved free
  // users often have those false for normal availability reasons). Once the
  // selected domain is APPROVED, never show the upgrade-to-skip-validation CTA.
  const isPendingUpgradePreview = Boolean(
    !isPro &&
      domain?.status !== 'APPROVED' &&
      (upgradeCta || profile?.profileValidation?.status === 'pending')
  );

  const onChangeSearchInputValue = (e) => {
    setSearchInputValue(e.target.value);
  };

  const openRequestModal = (dom, type) => {
    setRequestType(type);
    setRequestModalOpen(dom);
  };
  const setRequestModalClose = () => {
    setRequestModalOpen(null);
    listOpportunitiesCall();
  };

  const ownDr = domain?.metrics?.authority?.dr;
  const getIsGoodMatch = (opportunity) => {
    const categoriesInCommon = domain.categories.filter((value) =>
      opportunity.categories.includes(value)
    );
    const hasCategoryMatch = categoriesInCommon.length > 0;
    const oppDr = opportunity?.metrics?.authority?.dr;
    if (ownDr == null || oppDr == null) {
      return hasCategoryMatch;
    }
    const drDifference = Math.abs(ownDr - oppDr);
    const hasLanguageMatch =
      domain.language &&
      opportunity.language &&
      domain.language === opportunity.language;
    const maxDrTolerance = hasLanguageMatch ? 15 : 10;

    if (drDifference <= maxDrTolerance && hasCategoryMatch) {
      return true;
    }
    return false;
  };

  const onChangeOnlyGoodMatch = (e) => {
    // setOnlyGoodMatchFilter(!onlyGoodMatchFilter);
    setSearchParams((prev) => {
      prev.set('onlyGoodMatch', e.target.checked);
      return prev;
    });
  };

  const onChangeOnlyAvailable = (e) => {
    const checked =
      typeof e?.target?.checked === 'boolean'
        ? e.target.checked
        : !onlyAvailableFilter;
    setOnlyAvailableFilter(checked);
    setSearchParams((prev) => {
      prev.set('onlyAvailable', String(checked));
      return prev;
    });
  };

  const onChangeOnlyYC = (e) => {
    // setOnlyGoodMatchFilter(!onlyGoodMatchFilter);
    setSearchParams((prev) => {
      prev.set('onlyYC', e.target.checked);
      return prev;
    });
  };

  const onChangeHideSlowResponders = (e) => {
    setSearchParams((prev) => {
      prev.set('hideSlowResponders', e.target.checked);
      return prev;
    });
  };

  const revealDomain = (oppId) => {
    reveal({ domain: domain.name, opportunityId: oppId })
      .then((res) => {
        const currentRevealsRemaining = domain.reveals.remaining;
        updateDomains();
        setTimeout(() => {
          refreshDomainDetails();
          listOpportunitiesCall();
          setTimeout(() => {
            message.success(
              `Domain revealed successfully. You have ${
                currentRevealsRemaining > 1 ? currentRevealsRemaining - 1 : 'no'
              } reveals remaining.`
            );
          }, 300);
        }, 500);
      })
      .catch((e) => {
        console.error(e);
        if (handleDomainAccessError(e, { proPlansEnabled })) {
          return;
        }
        if (e.response.data.error === 'CROSS_TIER_NOT_ALLOWED') {
          if (proPlansEnabled && !isPro) {
            message.info(
              'This domain is in a different DR tier. Upgrade to Pro to access all tiers.'
            );
          } else {
            message.error('This domain is no longer available for exchange.');
          }
          listOpportunitiesCall();
        } else if (e.response.data.error === 'REVEAL_THRESHOLD_REACHED') {
          message.error(
            'You have reached the reveal threshold. Exchange a backlink to reset the quota.'
          );
        } else if (e.response.data.error === 'NOT_ENOUGH_POINTS') {
          message.error("You don't have enough points to reveal this domain.");
        }
      });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.filters}>
        <div className={styles.filter}>
          <TreeSelect
            className={styles.categorySelectFilter}
            allowClear={true}
            prefix={<FilterOutlined />}
            onChange={onChange}
            multiple
            style={{ width: 400, maxWidth: '100%' }}
            fieldNames={{
              children: 'subCategories',
              value: '_id',
              label: 'name',
            }}
            placeholder="Select a category"
            showSearch
            treeData={categories}
            filterTreeNode={(input, node) =>
              node.name.toLowerCase().includes(input)
            }
            treeExpandAction={'click'}
            value={selectedCategories}
            disabled={!opportunities}
          />
        </div>
        <div className={styles.filter}>
          <Input
            placeholder="Search keywords from description"
            value={searchInputValue}
            onChange={onChangeSearchInputValue}
            prefix={<SearchOutlined />}
            className={styles.searchInput}
            allowClear={true}
          />
        </div>

        <div className={styles.filter}>
          <Tooltip
            title={
              <div>
                Good match domains have at least one category in common and has
                a DR close to yours.
              </div>
            }
          >
            <div
              className={styles.onlyGoodMatchButton}
              onClick={onChangeOnlyGoodMatch}
            >
              <Checkbox
                checked={onlyGoodMatchFilter}
                style={{ marginRight: 6 }}
              />
              <LikeFilled style={{ marginRight: 4 }} />
              Only Good Matches
            </div>
          </Tooltip>
        </div>
        <div className={styles.filter}>
          <div
            className={styles.onlyAvailableButton}
            onClick={onChangeOnlyAvailable}
          >
            <Checkbox
              checked={onlyAvailableFilter}
              style={{ marginRight: 6 }}
            />
            <CheckCircleFilled style={{ marginRight: 4 }} />
            Only Available
          </div>
        </div>
        {domain.isYC && (
          <div className={styles.filter}>
            <Tooltip title={<div>Filter only YC companies.</div>}>
              <div className={styles.onlyYCButton} onClick={onChangeOnlyYC}>
                <Checkbox checked={onlyYCFilter} style={{ marginRight: 6 }} />
                <img src="/img/yc.png" className={styles.ycLogo} alt="yc" />
                Only YC companies&nbsp;
                <InfoCircleFilled />
              </div>
            </Tooltip>
          </div>
        )}
        <div className={styles.filter}>
          <Tooltip title={<div>Hide domains that are slow to respond to requests.</div>}>
            <div
              className={styles.hideSlowRespondersButton}
              onClick={onChangeHideSlowResponders}
            >
              <Checkbox
                checked={hideSlowResponders}
                style={{ marginRight: 6 }}
              />
              <HourglassFilled style={{ marginRight: 4 }} />
              Hide Slow Responders
            </div>
          </Tooltip>
        </div>
        <div className={styles.filter}>
          <a
            onClick={() => {
              //add search params domain.categories
              setSearchParams((prev) => {
                prev.set('categories', domain.categories);
                return prev;
              });
            }}
          >
            <FilterOutlined /> Only my categories
          </a>
        </div>
        <div className={styles.filterRight}>
          {filteredOpportunities && (
            <div className={styles.filter}>
              <span>{`${filteredOpportunities.length} ${pluralize(
                'result',
                filteredOpportunities.length
              )}`}</span>
            </div>
          )}
        </div>
      </div>
      {filteredOpportunities?.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            opportunities?.length === 0 ? (
              <div>
                <div>No matching opportunities available yet.</div>
                <div style={{ marginTop: 8, color: '#888', fontSize: 13 }}>
                  New matching domains are added weekly — check back soon.
                </div>
              </div>
            ) : (
              'No opportunities match your current filters, try adjusting them.'
            )
          }
        />
      )}
      {metricsPending && !upgradeCta && (
        <div className={styles.metricsPendingNote}>
          Domain metrics are still loading. You can browse opportunities now;
          Ask / Give unlock once metrics are ready.
        </div>
      )}
      {(() => {
        const list = filteredOpportunities || [];
        const LAST_ROW_COUNT = 3;
        const blurLastRow =
          upgradeCta && !loadingOpportunities && list.length > 0;
        const lastRowCount = blurLastRow
          ? Math.min(LAST_ROW_COUNT, list.length)
          : 0;
        const clearOpportunities = blurLastRow
          ? list.slice(0, list.length - lastRowCount)
          : list;
        const lastRowOpportunities = blurLastRow
          ? list.slice(list.length - lastRowCount)
          : [];

        const renderOpportunityCard = (element) => {
          const categoriesDomain = categoriesFlat
            ? element.categories.map((r) =>
                categoriesFlat?.find((c) => c._id === r)
              )
            : element.categories;

          const isGoodMatch = getIsGoodMatch(element);
          const elementDr = element?.metrics?.authority?.dr;

          return (
            <div className={styles.vignetteWrapper} key={element._id}>
              <div className={styles.vignetteInner}>
                <div className={styles.domainDetailsWrapper}>
                  <DomainMetrics
                    revealDomain={revealDomain}
                    domain={element}
                    categories={categoriesDomain}
                    isGoodMatch={isGoodMatch}
                    canSeeYCCompanies={domain.isYC}
                    revealsRemaining={domain?.reveals?.remaining}
                  />
                </div>

                <div className={styles.vignetteCTAs}>
                  <div className={styles.vignetteCTA}>
                    <div className={styles.vignetteCTAInner}>
                      {(() => {
                        const notEnoughPoints =
                          elementDr != null && elementDr > domain.points;
                        const pendingPreview = isPendingUpgradePreview;
                        const isDisabled = pendingPreview
                          ? hasActiveBacklinkRequest(element)
                          : hasActiveBacklinkRequest(element) ||
                            (!element.canReceive && !notEnoughPoints);

                        return (
                          <Tooltip
                            title={
                              hasActiveBacklinkRequest(element)
                                ? 'You already exchanged backlinks with this domain'
                                : !pendingPreview &&
                                  !element.canReceive &&
                                  !notEnoughPoints
                                ? 'You reached your maximum active requests (2)'
                                : ''
                            }
                          >
                            <Button
                              block
                              disabled={isDisabled}
                              onClick={() =>
                                openRequestModal(element, REQUEST_TYPE.ASK)
                              }
                            >
                              Ask
                              {elementDr != null && (
                                <b>{`  -${elementDr} `}</b>
                              )}
                              <KarmaIcon fade={isDisabled} />
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </div>
                  <div className={styles.vignetteCTA}>
                    <div className={styles.vignetteCTAInner}>
                      {(() => {
                        const pendingPreview = isPendingUpgradePreview;
                        const isDisabled = pendingPreview
                          ? hasActiveBacklinkRequest(element)
                          : !element.canGive ||
                            hasActiveBacklinkRequest(element) ||
                            domain.pendingGiveRequestsCount >=
                              MAX_CONCURRENT_REQUESTS;

                        return (
                          <Tooltip
                            title={
                              hasActiveBacklinkRequest(element)
                                ? 'You already exchanged backlinks with this domain'
                                : pendingPreview
                                ? ''
                                : domain.pendingGiveRequestsCount >=
                                  MAX_CONCURRENT_REQUESTS
                                ? 'You have reached your maximum active requests (2/2)'
                                : !element.canGive
                                ? "This website can't receive requests at the moment. They may not have enough points."
                                : ''
                            }
                          >
                            <Button
                              type="primary"
                              block
                              disabled={isDisabled}
                              onClick={() =>
                                openRequestModal(element, REQUEST_TYPE.GIVE)
                              }
                            >
                              Give
                              {ownDr != null && <b>{`  +${ownDr} `}</b>}
                              <KarmaIcon fade={isDisabled} />
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        };

        const upgradeToProButton = proPlansEnabled ? (
          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            loading={checkoutLoading}
            onClick={handleUpgradeToPro}
          >
            Upgrade to Pro
          </Button>
        ) : (
          <Button type="primary" size="large" href="/#/plan">
            Upgrade to Pro
          </Button>
        );

        return (
          <>
      <div className={styles.list}>
        {clearOpportunities.map(renderOpportunityCard)}
        {lastRowOpportunities.length > 0 && (
          <div className={styles.blurredLastRow}>
            <div className={styles.blurredLastRowCards} aria-hidden="true">
              {lastRowOpportunities.map(renderOpportunityCard)}
            </div>
            <div className={styles.blurredLastRowOverlay}>
              <div className={styles.blurredTeaserCta}>
                <div className={styles.blurredTeaserTitle}>
                  Upgrade to Pro to skip validation
                </div>
                <div className={styles.blurredTeaserSubtitle}>
                  Unlock the full marketplace while your profile is reviewed
                </div>
                {upgradeToProButton}
              </div>
            </div>
          </div>
        )}
        <RequestBacklinkModal
          domain={domain}
          opportunity={requestModalOpen}
          isOpen={!!requestModalOpen}
          onClose={setRequestModalClose}
          categories={categoriesFlat}
          type={requestType}
          refreshDomainDetails={refreshDomainDetails}
          proPlansEnabled={proPlansEnabled}
          upgradeCta={isPendingUpgradePreview}
          isPro={isPro}
        />
        {loadingOpportunities && <Loader />}
      </div>
      {upgradeCta && !loadingOpportunities && (
        <div className={styles.upgradePreviewCta}>
          <div className={styles.upgradePreviewTitle}>
            You’re viewing a preview of opportunities
          </div>
          <div className={styles.upgradePreviewDescription}>
            Upgrade to Pro for the full marketplace while your profile is being
            validated — no wait for admin approval.
          </div>
          {upgradeToProButton}
        </div>
      )}
          </>
        );
      })()}
    </div>
  );
};

export default BrowseBacklinksOpportunities;
